import { css } from '@emotion/react';

import {
  type SelectableOptionCardTone,
  type SelectableOptionCardVariant,
} from './SelectableOptionCard.types';

export const cardStyles = (
  selected: boolean,
  disabled: boolean,
  variant: SelectableOptionCardVariant = 'default',
  tone: SelectableOptionCardTone = 'default'
) => {
  const isPlus = variant === 'plus';
  const isNeutralSelected = selected && tone === 'neutral' && !isPlus;

  const borderColor = isPlus
    ? 'var(--color-gold-500)'
    : selected
    ? 'var(--border-action)'
    : 'var(--border-primary)';

  const background = isPlus
    ? 'linear-gradient(to right, rgba(255, 187, 82, 0.15), rgba(255, 255, 255, 0.05))'
    : isNeutralSelected
    ? 'var(--surface-neutral)'
    : selected
    ? 'var(--surface-success)'
    : 'var(--surface-page)';

  const hoverBorderColor = isPlus
    ? 'var(--color-gold-500)'
    : selected
    ? 'var(--border-action-hover)'
    : 'var(--border-disabled)';

  const focusOutline = isPlus ? 'var(--color-gold-500)' : 'var(--border-focus)';

  return css`
    display: block;
    width: 100%;
    min-width: 0;
    padding: var(--spacing-4);
    border: 1px solid ${borderColor};
    border-radius: var(--spacing-4);
    background: ${background};
    color: var(--text-primary);
    font: inherit;
    text-align: left;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    appearance: none;
    /* Plus + disabled = included perk — keep full opacity so it doesn't look inactive. */
    opacity: ${disabled && !isPlus ? 0.6 : 1};
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:hover:not(:disabled) {
      border-color: ${hoverBorderColor};
    }

    &:focus-visible {
      outline: 2px solid ${focusOutline};
      outline-offset: 2px;
    }
  `;
};

export const checkIndicatorStyles = css`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border-radius: 999px;
  background: var(--color-gold-600);
  color: var(--color-base-white);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

export const radioStyles = (selected: boolean) => css`
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border: 2px solid
    ${selected ? 'var(--border-action)' : 'var(--border-primary)'};
  border-radius: 999px;
  background: var(--surface-page);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 999px;
    background: var(--surface-action);
    opacity: ${selected ? 1 : 0};
    transform: scale(${selected ? 1 : 0.6});
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
`;
